import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';

const batteryTypes = [
  {
    icon: 'ri-car-line',
    label: 'Car & Truck Batteries',
    desc: 'Lead-acid 12V batteries from all vehicle makes and models, including trucks, SUVs, and vans',
    tag: 'Most Common',
  },
  {
    icon: 'ri-sun-line',
    label: 'Solar Batteries',
    desc: 'Deep-cycle batteries from residential and commercial solar energy storage systems (LiFePO4, AGM, GEL)',
    tag: 'High Volume OK',
  },
  {
    icon: 'ri-charging-pile-line',
    label: 'EV Charger & EV Batteries',
    desc: 'Electric vehicle charger station batteries, EV auxiliary packs, and lithium-ion EV battery modules',
    tag: 'Free Pickup Available',
  },
  {
    icon: 'ri-flashlight-line',
    label: 'Forklift & Industrial Batteries',
    desc: '24V, 36V, and 48V industrial forklift batteries, warehouse equipment batteries, and floor scrubber packs',
    tag: 'Free Pickup Available',
  },
  {
    icon: 'ri-battery-charge-line',
    label: 'Lithium-Ion Packs',
    desc: 'Laptop batteries, power tool packs, e-bike batteries, scooter batteries, and portable power stations',
    tag: null,
  },
  {
    icon: 'ri-smartphone-line',
    label: 'Phone & Tablet Batteries',
    desc: 'iPhone, Android, and all mobile device batteries — intact or removed from the device',
    tag: null,
  },
  {
    icon: 'ri-shield-line',
    label: 'UPS & Backup Batteries',
    desc: 'SLA, VRLA, and AGM batteries from UPS systems, security systems, and emergency lighting',
    tag: null,
  },
  {
    icon: 'ri-motorbike-line',
    label: 'Motorcycle & Marine',
    desc: 'Motorcycle, ATV, golf cart, boat, RV, and powersport lead-acid batteries',
    tag: null,
  },
  {
    icon: 'ri-battery-line',
    label: 'Household Batteries',
    desc: 'AA, AAA, C, D, 9V alkaline, NiMH rechargeable, button cell, and lithium coin batteries',
    tag: null,
  },
];

const serviceAreas = [
  'Downtown Los Angeles', 'Hollywood', 'Silver Lake', 'Echo Park',
  'Santa Monica', 'Venice', 'Culver City', 'West Los Angeles',
  'Burbank', 'Glendale', 'Pasadena', 'Monrovia',
  'Long Beach', 'Torrance', 'Redondo Beach', 'Hawthorne',
  'Inglewood', 'Compton', 'Gardena', 'Carson',
  'Van Nuys', 'Sherman Oaks', 'North Hollywood', 'Studio City',
  'El Monte', 'Baldwin Park', 'Pomona', 'Ontario',
  'Anaheim', 'Orange', 'Santa Ana', 'Irvine',
];

const dangerFacts = [
  {
    icon: 'ri-fire-line',
    title: 'Fire Hazard',
    desc: 'Lithium batteries in landfills ignite. California recorded 300+ battery-related waste fires in 2023 alone.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Toxic Leaching',
    desc: 'Lead, mercury, cadmium, and lithium from discarded batteries contaminate LA groundwater for decades.',
  },
  {
    icon: 'ri-scales-line',
    title: 'California Law',
    desc: 'AB 2901 prohibits trashing batteries in California. Fines can reach $25,000 per violation.',
  },
  {
    icon: 'ri-recycle-line',
    title: '95% Recoverable',
    desc: 'Nearly all battery materials — lead, lithium, cobalt — can be recovered and reused in new batteries.',
  },
];

const faqs = [
  {
    q: 'Do you offer free battery pickup in Los Angeles?',
    a: 'Yes! We offer FREE battery pickup anywhere in Los Angeles and surrounding areas when you have 100 batteries or more. This includes car batteries, solar batteries, industrial batteries, lithium-ion packs, and mixed battery loads. Call (213) 600-1000 to schedule.',
  },
  {
    q: 'Do you recycle solar energy storage batteries?',
    a: 'Absolutely. We recycle all types of solar batteries including LiFePO4 (lithium iron phosphate), AGM, GEL, and flooded deep-cycle batteries used in residential and commercial solar systems. High-volume solar battery pickups are available free of charge.',
  },
  {
    q: 'Can you pick up EV charger station batteries?',
    a: 'Yes. We handle EV charger machine batteries, EV auxiliary battery packs, and lithium-ion EV modules. Our team has the equipment and certifications to safely handle large-format lithium battery systems from charging stations and electric vehicle fleets.',
  },
  {
    q: 'Where can I drop off car batteries in Los Angeles?',
    a: 'Drop off car batteries for free at our two LA locations: 2542 W. 3rd Street, Los Angeles CA 90057 and 4902 W. Slauson Ave, Los Angeles CA 90056. No appointment needed, no charge. We accept all sizes and conditions.',
  },
  {
    q: 'What areas do you serve for battery recycling?',
    a: 'We serve all of Los Angeles County and much of Southern California, including Downtown LA, Santa Monica, Burbank, Glendale, Pasadena, Long Beach, Torrance, Inglewood, Van Nuys, and surrounding cities. Call to confirm service in your area.',
  },
  {
    q: 'Are lithium-ion batteries dangerous to recycle?',
    a: 'Lithium-ion batteries require special handling because they can ignite if punctured or short-circuited. Our certified technicians are trained to handle all battery types safely per California environmental regulations. Never put lithium batteries in regular trash.',
  },
  {
    q: 'Is battery recycling free at your drop-off locations?',
    a: 'Yes — drop-off is always free at both Los Angeles locations for all battery types. Free pickup is available for qualifying loads of 100 batteries or more anywhere in the LA area.',
  },
];

const BatteryRecyclingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

  return (
    <>
      <title>Battery Recycling Los Angeles | Car, Solar & EV Batteries | Free Pickup 100+ | Greenly</title>
      <meta name="description" content="Free battery recycling in Los Angeles — car, solar, EV & lithium-ion batteries accepted. FREE pickup for 100+ batteries across LA County. CalRecycle certified. Call (213) 600-1000." />
      <meta name="keywords" content="battery recycling Los Angeles, car battery recycling LA, solar battery recycling Los Angeles, EV battery recycling LA, free battery pickup Los Angeles" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/battery-recycling`} />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${SITE_URL}/battery-recycling#webpage`,
            url: `${SITE_URL}/battery-recycling`,
            name: 'Battery Recycling Los Angeles | Car, Solar & EV Batteries | Free Pickup 100+ | Greenly',
            description: 'Battery recycling in Los Angeles — car batteries, solar batteries, EV charger batteries, lithium-ion & all types. Free drop-off. FREE pickup for 100+ batteries across LA County.',
            isPartOf: { '@id': `${SITE_URL}/#website` },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
                { '@type': 'ListItem', position: 2, name: 'Battery Recycling Los Angeles', item: `${SITE_URL}/battery-recycling` },
              ],
            },
          },
          {
            '@type': 'Service',
            '@id': `${SITE_URL}/battery-recycling#service`,
            name: 'Battery Recycling Los Angeles',
            description: 'Free battery recycling in Los Angeles. Car batteries, solar batteries, EV charger batteries, lithium-ion, and all battery types accepted. Free pickup for 100+ batteries anywhere in LA County.',
            url: `${SITE_URL}/battery-recycling`,
            provider: { '@id': `${SITE_URL}/#business` },
            areaServed: { '@type': 'AdministrativeArea', name: 'Los Angeles County' },
            serviceType: 'Battery Recycling',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free battery recycling drop-off and free pickup for 100+ batteries' },
          },
          {
            '@type': 'FAQPage',
            '@id': `${SITE_URL}/battery-recycling#faq`,
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          },
        ],
      })}</script>

      <Navbar />
      <main>

        {/* ── HERO ── */}
        <section className="relative bg-gray-900 pt-24 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/Hard-Drive-Shredding.jpg"
              alt="Battery recycling Los Angeles — car batteries solar batteries and EV batteries accepted"
              className="w-full h-full object-cover object-top opacity-30"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-gray-900/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Battery Recycling Los Angeles' }]} />
            <div className="max-w-2xl mt-8">
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  <i className="ri-battery-charge-line"></i>Free Battery Recycling
                </span>
                <span className="inline-flex items-center gap-1.5 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  <i className="ri-truck-line"></i>FREE Pickup — 100+ Batteries
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                Battery Recycling<br />
                <span className="text-[#74C69D]">in Los Angeles</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                We recycle <strong className="text-white">car batteries, solar batteries, EV charger batteries, lithium-ion packs, and every battery type</strong> — serving all of Los Angeles and surrounding areas. Free drop-off. <span className="text-amber-400 font-bold">FREE pickup for 100 batteries or more.</span>
              </p>
              <div className="flex flex-wrap gap-3 mb-7">
                <a
                  href="tel:+12136001000"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-fill"></i>(213) 600-1000
                </a>
                <Link
                  to="/pickup-request"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-truck-line"></i>Request Free Pickup
                </Link>
              </div>
              <div className="flex flex-wrap gap-5">
                {['All Battery Types', 'Free Drop-Off', 'Free Pickup (100+)', 'CalRecycle Certified'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-green-300 text-xs font-semibold">
                    <i className="ri-check-line text-[#74C69D]"></i>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── GREEN TRUST BAR ── */}
        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              {[
                { icon: 'ri-map-pin-line', text: 'All of LA County & Surrounding Areas' },
                { icon: 'ri-car-line', text: 'Car, Solar & EV Batteries' },
                { icon: 'ri-truck-line', text: 'Free Pickup for 100+ Batteries' },
                { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
              ].map((i) => (
                <div key={i.text} className="flex items-center gap-2">
                  <i className={`${i.icon} text-[#74C69D]`}></i>
                  <span className="font-semibold">{i.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FREE PICKUP CALLOUT ── */}
        <section className="py-14 bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="w-full lg:w-1/2">
                <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                  <i className="ri-truck-line"></i>FREE PICKUP PROGRAM
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                  Free Battery Pickup<br />
                  <span className="text-amber-600">for 100 Batteries or More</span>
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Have a large battery load? We come to you — at no charge. Whether it&apos;s a fleet of car batteries, a solar installation removal, a warehouse of forklift packs, or an EV charger station decommission — if you have <strong className="text-gray-900">100 batteries or more</strong>, we pick them up free of charge anywhere in Los Angeles and surrounding areas.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    'Automotive shops & car dealerships',
                    'Solar installation companies',
                    'EV charging network operators',
                    'Warehouses & logistics centers',
                    'Property managers & buildings',
                    'Municipalities & government fleets',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-amber-500 font-bold"></i>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+12136001000"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-phone-fill"></i>Call to Schedule Pickup
                  </a>
                  <Link
                    to="/pickup-request"
                    className="inline-flex items-center gap-2 bg-white border border-amber-300 text-amber-700 font-bold px-6 py-3 rounded-md hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>Fill Out Request Form
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Fleet%20of%20white%20recycling%20trucks%20loading%20large%20pallets%20stacked%20with%20used%20car%20batteries%20and%20solar%20energy%20storage%20batteries%20outside%20a%20Los%20Angeles%20industrial%20warehouse%20on%20a%20bright%20sunny%20day%2C%20workers%20in%20safety%20vests%20and%20gloves%20organizing%20battery%20loads%20on%20forklifts%2C%20professional%20battery%20collection%20service%2C%20amber%20orange%20and%20white%20color%20scheme&width=700&height=480&seq=battv2pickup001&orientation=landscape"
                    alt="Free battery pickup service in Los Angeles for 100 or more batteries"
                    className="w-full h-72 object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/95 rounded-xl px-5 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl shrink-0">
                        <i className="ri-truck-line text-white text-lg"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">FREE Pickup — No Hidden Fees</div>
                        <div className="text-gray-500 text-xs">100+ batteries · All of Los Angeles &amp; surrounding areas</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── BATTERY TYPES ── */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Accept</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                All Battery Types Recycled<br />
                <strong className="text-[#1B6B3A]">Across Los Angeles</strong>
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
                From a single AA battery to an entire solar storage system — we accept everything, safely and for free.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {batteryTypes.map((t) => (
                <div key={t.label} className="bg-[#F4FAF7] rounded-xl p-6 flex items-start gap-4 relative">
                  {t.tag && (
                    <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                      {t.tag}
                    </span>
                  )}
                  <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl shrink-0 border border-gray-100">
                    <i className={`${t.icon} text-[#1B6B3A] text-xl`}></i>
                  </div>
                  <div className="pr-12">
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{t.label}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY IT MATTERS ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4">
                {dangerFacts.map((f) => (
                  <div key={f.title} className="bg-white rounded-xl p-5">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#1B6B3A] rounded-lg mb-3">
                      <i className={`${f.icon} text-white text-sm`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="w-full lg:w-1/2">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Why It Matters</div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5">
                  Why Proper <strong>Battery Recycling</strong><br />Matters for Los Angeles
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  As solar installations and electric vehicles explode across Southern California, the volume of end-of-life <strong className="text-gray-900">solar batteries and EV batteries</strong> is growing fast. These contain lithium, cobalt, and nickel — materials that are both toxic and extremely valuable when properly recovered.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  California&apos;s <strong className="text-gray-900">AB 2901</strong> makes it illegal to dispose of rechargeable batteries, car batteries, or lithium-ion batteries in regular trash. Violations can reach $25,000 per incident.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Greenly provides a certified, safe, and free recycling solution that keeps hazardous materials out of LA&apos;s water supply — while recovering valuable materials for reuse.
                </p>
                <Link
                  to="/pickup-request"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-truck-line"></i>Request Battery Pickup
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Simple Process</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">How Battery Recycling Works</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '01',
                  icon: 'ri-phone-line',
                  title: 'Call or Submit',
                  desc: 'Call (213) 600-1000 or fill out our pickup request form online. Tell us what batteries you have.',
                },
                {
                  step: '02',
                  icon: 'ri-calendar-check-line',
                  title: 'We Schedule',
                  desc: 'We confirm your location, volume, and a pickup time that works for your schedule across all LA areas.',
                },
                {
                  step: '03',
                  icon: 'ri-truck-line',
                  title: 'We Pick Up Free',
                  desc: 'Our certified team arrives with the right equipment and loads your batteries — completely free for 100+ units.',
                },
                {
                  step: '04',
                  icon: 'ri-recycle-line',
                  title: 'Certified Recycling',
                  desc: 'We recycle all batteries per California environmental regulations and provide documentation on request.',
                },
              ].map((s) => (
                <div key={s.step} className="flex flex-col items-start">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className={`${s.icon} text-white text-lg`}></i>
                    </div>
                    <span className="text-3xl font-black text-gray-100">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SERVICE AREAS ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Coverage Area</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Battery Recycling Across<br />
                <strong className="text-[#1B6B3A]">Los Angeles &amp; Surrounding Cities</strong>
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                We serve the entire LA metro area — free drop-off at both our LA locations, and free pickup for 100+ batteries anywhere in the region.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5 justify-center mb-10">
              {serviceAreas.map((area) => (
                <span key={area} className="flex items-center gap-1.5 bg-white border border-gray-200 px-3 py-1.5 rounded-full text-xs font-medium text-gray-700">
                  <i className="ri-map-pin-line text-[#1B6B3A] text-xs"></i>{area}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Drop-Off Location 1</div>
                    <div className="text-gray-600 text-sm mt-1">2542 W. 3rd Street, Los Angeles, CA 90057</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a
                  href="https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer"
                >
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Drop-Off Location 2</div>
                    <div className="text-gray-600 text-sm mt-1">4902 W. Slauson Ave, Los Angeles, CA 90056</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a
                  href="https://maps.google.com/?q=4902+W+Slauson+Ave+Los+Angeles+CA+90056"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer"
                >
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Battery Recycling FAQs — Los Angeles</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-[#F4FAF7] border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-[#E8F5EE] transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 bg-white">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CROSS-LINKS ── */}
        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Recycle More at the Same Drop-Off</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: 'TV Recycling', path: '/tv-recycling', icon: 'ri-tv-line' },
                { label: 'Computer Recycling', path: '/computer-recycling', icon: 'ri-computer-line' },
                { label: 'Hard Drive Destruction', path: '/hard-drive-destruction', icon: 'ri-hard-drive-line' },
                { label: 'All Electronics', path: '/services', icon: 'ri-recycle-line' },
              ].map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-5 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={s.icon}></i>{s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 bg-[#1B6B3A] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20green%20circuit%20board%20pattern%20with%20battery%20icons%20and%20recycling%20symbols%20overlapping%2C%20dark%20forest%20green%20background%2C%20minimal%20geometric%20lines%2C%20tech%20pattern%20texture&width=1440&height=400&seq=battv2cta001&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
              aria-hidden="true"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              <i className="ri-truck-line"></i>FREE PICKUP · 100+ BATTERIES
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Recycle Your Batteries in Los Angeles?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Don&apos;t trash your batteries — it&apos;s illegal in California and dangerous for our environment. Drop off free at either LA location, or call for free pickup of 100 batteries or more anywhere in the LA area.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>Request Free Pickup
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-line"></i>Get Directions
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default BatteryRecyclingPage;
